import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
    padding: "1.5rem",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardBody: { padding: "1rem 1.125rem" },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 4,
  },
  kpiValue: { fontSize: 16, fontWeight: 700, color: "#1a1d23" },
  fieldLabel: { fontSize: 11, color: "#9ca3af", marginBottom: 2 },
  fieldValue: { fontSize: 13, fontWeight: 600, color: "#1a1d23" },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      textTransform: "uppercase",
      ...(map[variant] || map.gray),
    };
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
  },
  btn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 6,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: "1rem",
  },
  warnBanner: {
    fontSize: 13,
    color: "#92400E",
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: "1rem",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return "-";
  return new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

const statusPillVariant = (status) => {
  if (status === "active") return "green";
  if (status === "prospective") return "blue";
  if (status === "past") return "gray";
  if (status === "evicted") return "red";
  return "gray";
};

const paymentPillVariant = (status) => {
  if (status === "paid") return "green";
  if (status === "late") return "amber";
  if (status === "overdue") return "red";
  if (status === "partial") return "blue";
  return "gray";
};

const applicationPillVariant = (status) => {
  if (status === "approved") return "green";
  if (status === "pending") return "amber";
  if (status === "under_review") return "blue";
  if (status === "rejected") return "red";
  if (status === "withdrawn") return "gray";
  return "gray";
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const FieldRow = ({ label, value }) => (
  <div style={{ marginBottom: "0.75rem" }}>
    <div style={s.fieldLabel}>{label}</div>
    <div style={s.fieldValue}>{value ?? "—"}</div>
  </div>
);

const PageSpinner = ({ text }) => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem", fontSize: 14, color: "#6b7280", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <span style={s.spinner} />{text}
    </div>
  </>
);

// ── Main component ────────────────────────────────────────────────────────────
function TenantDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantApplications, setTenantApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(API_ENDPOINTS.tenants.getById(id), {
          headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        const data = await response.json();
        setTenant(data?.data || null);
      } catch (err) {
        console.error("Error fetching tenant details:", err);
        setError(err.message || "Failed to load tenant details");
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [id, getAuthHeaders]);

  useEffect(() => {
    if (!id) { setTenantApplications([]); return; }
    const fetchTenantApplications = async () => {
      try {
        setApplicationsLoading(true);
        const response = await fetch(
          `${API_ENDPOINTS.applications.getAll}?tenantId=${encodeURIComponent(id)}&limit=50`,
          { headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true", ...getAuthHeaders() } }
        );
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        const data = await response.json();
        setTenantApplications(Array.isArray(data?.data) ? data.data : []);
      } catch (fetchError) {
        console.error("Error fetching tenant applications:", fetchError);
        setTenantApplications([]);
      } finally {
        setApplicationsLoading(false);
      }
    };
    fetchTenantApplications();
  }, [id, getAuthHeaders]);

  const sortedLeaseHistory = useMemo(() => {
    if (!tenant?.leaseHistory) return [];
    return [...tenant.leaseHistory].sort((a, b) => new Date(b.leaseStart) - new Date(a.leaseStart));
  }, [tenant]);

  const sortedPayments = useMemo(() => {
    if (!tenant?.paymentHistory) return [];
    return [...tenant.paymentHistory].sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
  }, [tenant]);

  const sortedApplications = useMemo(() => {
    if (!tenantApplications?.length) return [];
    return [...tenantApplications].sort(
      (a, b) => new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt)
    );
  }, [tenantApplications]);

  const handleBackNavigation = () => {
    if (window.history.length > 1) { navigate(-1); return; }
    navigate("/tenants");
  };

  const getPropertyId = (property) => {
    if (!property) return "";
    if (typeof property === "string") return property;
    return property?._id?.toString?.() || "";
  };

  const goToPropertyDetails = (property) => {
    const propertyId = getPropertyId(property);
    if (!propertyId) return;
    navigate(`/properties/propertyDetails/${propertyId}`);
  };

  if (loading) return <PageSpinner text="Loading tenant details…" />;

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.errorBanner}>
          <strong>Unable to load tenant details</strong>
          <p style={{ margin: "4px 0 8px" }}>{error}</p>
          <button style={s.btn} onClick={handleBackNavigation}>← Back to tenants</button>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.warnBanner}>
          <strong>Tenant not found</strong>
          <p style={{ margin: "4px 0 8px" }}>The requested tenant could not be found.</p>
          <button style={s.btn} onClick={handleBackNavigation}>← Back to tenants</button>
        </div>
      </div>
    );
  }

  const fullName = `${tenant.personalInfo?.firstName || ""} ${tenant.personalInfo?.lastName || ""}`.trim();
  const activeLease = tenant.activeLease;

  return (
    <div style={s.page} className="re-detail">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Page header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: "1.25rem" }}>
        <div>
          <button style={{ ...s.btn, marginBottom: 8 }} onClick={handleBackNavigation}>← Back</button>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1a1d23", marginBottom: 2 }}>
            {fullName || "Tenant details"}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>{tenant.personalInfo?.email || "-"}</div>
        </div>
        <Pill variant={statusPillVariant(tenant.status)}>{tenant.status || "unknown"}</Pill>
      </div>

      {/* ── KPI strip ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: "1.25rem" }}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Phone</div>
          <div style={s.kpiValue}>{tenant.personalInfo?.phone || "-"}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Date of Birth</div>
          <div style={s.kpiValue}>{formatDate(tenant.personalInfo?.dateOfBirth)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Monthly Income</div>
          <div style={{ ...s.kpiValue, color: "#166534" }}>{formatCurrency(tenant.employment?.monthlyIncome)}</div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.25rem" }}>
        {/* Left column (main) */}
        <div>
          {/* Current Lease */}
          <div style={s.card}>
            <div style={s.cardHead}>Current Lease</div>
            <div style={s.cardBody}>
              {activeLease ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.875rem" }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={s.fieldLabel}>Property</div>
                    <div style={{ ...s.fieldValue, fontSize: 14 }}>
                      {activeLease.property?.title || activeLease.property?.name || "N/A"}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {activeLease.property?.address?.street
                        ? `${activeLease.property.address.street}, ${activeLease.property.address.city || ""}`
                        : ""}
                    </div>
                  </div>
                  <FieldRow label="Monthly Rent" value={<span style={{ color: "#1E40AF" }}>{formatCurrency(activeLease.monthlyRent)}</span>} />
                  <FieldRow label="Security Deposit" value={formatCurrency(activeLease.securityDeposit)} />
                  <FieldRow label="Lease Start" value={formatDate(activeLease.leaseStart)} />
                  <FieldRow label="Lease End" value={formatDate(activeLease.leaseEnd)} />
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#9ca3af" }}>No active lease information available.</div>
              )}
            </div>
          </div>

          {/* Lease History */}
          <div style={s.card}>
            <div style={s.cardHead}>
              Lease History
              <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>{sortedLeaseHistory.length} records</span>
            </div>
            {sortedLeaseHistory.length === 0 ? (
              <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>No lease history found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Property", "Rent", "Start", "End", "Status"].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeaseHistory.map((lease) => {
                      const isTerminated = Boolean(lease.actualLeaseEnd);
                      const propertyId = getPropertyId(lease.property);
                      const isClickable = Boolean(propertyId);
                      return (
                        <tr
                          key={lease._id}
                          role={isClickable ? "button" : undefined}
                          tabIndex={isClickable ? 0 : undefined}
                          onClick={isClickable ? () => goToPropertyDetails(lease.property) : undefined}
                          onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToPropertyDetails(lease.property); } } : undefined}
                          style={isClickable ? { cursor: "pointer" } : undefined}
                          title={isClickable ? "Open property details" : undefined}
                        >
                          <td data-label="Property" style={s.td}>{lease.property?.title || lease.property?.name || "Untitled property"}</td>
                          <td data-label="Rent" style={s.td}>{formatCurrency(lease.monthlyRent)}</td>
                          <td data-label="Start" style={s.td}>{formatDate(lease.leaseStart)}</td>
                          <td data-label="End" style={s.td}>{formatDate(lease.actualLeaseEnd || lease.leaseEnd)}</td>
                          <td data-label="Status" style={s.td}>
                            <Pill variant={isTerminated ? "gray" : "green"}>
                              {isTerminated ? "Terminated" : "Completed/Active"}
                            </Pill>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div style={s.card}>
            <div style={s.cardHead}>
              Payment History
              <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>{sortedPayments.length} records</span>
            </div>
            {sortedPayments.length === 0 ? (
              <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>No payment records found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Period", "Property", "Amount", "Due Date", "Paid Date", "Status"].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPayments.map((payment) => (
                      <tr key={payment._id}>
                        <td data-label="Period" style={s.td}>
                          {new Date(payment.year, payment.month - 1).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                        </td>
                        <td data-label="Property" style={s.td}>
                          {payment.property?.title || payment.property?.buildingName || payment.property?.address?.street || "N/A"}
                        </td>
                        <td data-label="Amount" style={{ ...s.td, fontWeight: 600 }}>{formatCurrency(payment.amount)}</td>
                        <td data-label="Due Date" style={s.td}>{formatDate(payment.dueDate)}</td>
                        <td data-label="Paid Date" style={s.td}>{formatDate(payment.paidDate)}</td>
                        <td data-label="Status" style={s.td}>
                          <Pill variant={paymentPillVariant(payment.status)}>
                            {payment.status || "unknown"}
                          </Pill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Applications */}
          <div style={s.card}>
            <div style={s.cardHead}>
              Applications
              <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>{sortedApplications.length} records</span>
            </div>
            {applicationsLoading ? (
              <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>
                <span style={s.spinner} />Loading applications…
              </div>
            ) : sortedApplications.length === 0 ? (
              <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>No applications found for this tenant.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Application ID", "Property", "Submitted", "Status"].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedApplications.map((application) => (
                      <tr
                        key={application._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(application.viewPath || `/applications/${application._id}`)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(application.viewPath || `/applications/${application._id}`); } }}
                        style={{ cursor: "pointer" }}
                        title="Open application details"
                      >
                        <td data-label="Application ID" style={{ ...s.td, color: "#1E40AF", fontWeight: 500 }}>
                          {application.applicationId || application._id}
                        </td>
                        <td data-label="Property" style={s.td}>
                          {application.property?.title || application.property?.buildingName || application.property?.address?.street || "N/A"}
                        </td>
                        <td data-label="Submitted" style={s.td}>{formatDate(application.submittedAt || application.createdAt)}</td>
                        <td data-label="Status" style={s.td}>
                          <Pill variant={applicationPillVariant(application.status)}>
                            {application.status || "unknown"}
                          </Pill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.25rem", alignContent: "start" }}>
          {/* Employment */}
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={s.cardHead}>Employment</div>
            <div style={s.cardBody}>
              <FieldRow label="Employer" value={tenant.employment?.employer || "-"} />
              <FieldRow label="Job Title" value={tenant.employment?.jobTitle || "-"} />
              <FieldRow label="Employment Length" value={tenant.employment?.employmentLength || "-"} />
            </div>
          </div>

          {/* Emergency Contact */}
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={s.cardHead}>Emergency Contact</div>
            <div style={s.cardBody}>
              <FieldRow label="Name" value={tenant.personalInfo?.emergencyContact?.name || "-"} />
              <FieldRow label="Relationship" value={tenant.personalInfo?.emergencyContact?.relationship || "-"} />
              <FieldRow label="Phone" value={tenant.personalInfo?.emergencyContact?.phone || "-"} />
            </div>
          </div>

          {/* Additional Details */}
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={s.cardHead}>Additional Details</div>
            <div style={s.cardBody}>
              <FieldRow label="Credit Score" value={tenant.creditScore || "-"} />
              <FieldRow
                label="Background Check"
                value={
                  tenant.backgroundCheck?.completed
                    ? tenant.backgroundCheck?.passed
                      ? "Completed — Passed"
                      : "Completed — Review required"
                    : "Not completed"
                }
              />
              <FieldRow label="Created" value={formatDate(tenant.createdAt)} />
              <FieldRow label="Last Updated" value={formatDate(tenant.updatedAt)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TenantDetails;
